import React, { useState } from "react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { useMeterReasonCodesList } from "@features/mx/settings/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { Eye, Pencil, Trash, Plus } from "lucide-react";
import { Button } from "@shared/ui/button";
import { useNavigate } from "react-router-dom";
import { AddConditionReasonModal } from "../create/AddConditionReason";
import { PageHeader } from "@shared/ui/pageHeader";
import { PAGE_SIZE } from "@shared/utils/constants";

export function ConditionReasonsTab() {
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState<any>(null);

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const limit = PAGE_SIZE;

  const { data: reasonCodes, isLoading } = useMeterReasonCodesList(
    remoteUtilityId,
    page,
    limit,
    search
  );

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleSearch = (searchTerm: string) => {
    setSearch(searchTerm);
    setPage(1);
  };

  const pagination = {
    currentPage: page,
    totalPages: Math.ceil((reasonCodes?.count || 0) / PAGE_SIZE) || 1,
    totalItems: reasonCodes?.count || 0,
    startItem: (reasonCodes?.count || 0) === 0 ? 0 : (page - 1) * PAGE_SIZE + 1,
    endItem: Math.min(page * PAGE_SIZE, reasonCodes?.count || 0),
    itemsPerPage: PAGE_SIZE,
  };

  const tableData = React.useMemo(() => {
    const list = reasonCodes?.results || [];
    return list.map((r: any) => ({
      id: r.id.toString(),
      name: r.reason,
      resourceType: r.resourceTypeDisplay || "NA",
      subType: r.resourceSubtypeDisplay || "NA",
      resourceTypeCode: r.resourceType,
      resourceSubtypeCode: r.resourceSubtype,
      status: r.meterReadingStatus?.name,
      created: r.createdDate,
      reasonCode: r.reasonCode,
      meterReadingStatus: r.meterReadingStatus,
      isActive: r.isActive,
      statusId: r.meterReadingStatus?.id
    }));
  }, [reasonCodes]);

  const columns: TableColumn<any>[] = [
    { key: "name", header: "Reason Name", sortable: true },
    { key: "resourceType", header: "Resource Type", sortable: true },
    { key: "subType", header: "Sub Type", sortable: true },
    {
      key: "status",
      header: "Condition Status",
      sortable: true,
      render: (val: string) => (
        <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold border bg-blue-50 text-blue-700 border-blue-200">
          {val}
        </span>
      ),
    },
    { key: "created", header: "Created", sortable: true },
  ];

  const actions = [
    {
      label: "Edit",
      icon: <Pencil className="w-4 h-4 text-gray-700" />,
      onClick: (row: any) => {
        setSelectedReason(row);
        setIsAddOpen(true);
      },
    },
    {
      label: "Delete",
      icon: <Trash className="w-4 h-4 text-red-500" />,
      onClick: (row: any) => console.log("Delete", row),
      disabled: () => true
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <PageHeader
          title="Condition Reasons"
          description="Manage and configure conditional status reason codes"
        />
        <Button
          onClick={() => {
            setSelectedReason(null);
            setIsAddOpen(true);
          }}
          className="bg-primary text-white"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Conditional Reason
        </Button>
      </div>
      <div className="border border-gray-150 rounded-xl overflow-hidden p-4">
        <GenericTable
          columns={columns}
          data={tableData}
          rowKey="id"
          loading={isLoading}
          actions={actions}
          actionsType="icons"
          searchPlaceholder="Search condition reasons..."
          onSearch={handleSearch}
          pagination={pagination}
          onPageChange={handlePageChange}
        />
      </div>

      <AddConditionReasonModal
        open={isAddOpen}
        onOpenChange={(open) => {
          setIsAddOpen(open);
          if (!open) setSelectedReason(null);
        }}
        editData={selectedReason}
      />
    </div>
  );
}

export default ConditionReasonsTab;
